<template>
  <div>
    <div class="page-head">
      <div>
        <h1 class="page-head__title">{{ t("team.title") }}</h1>
        <p class="page-head__lede">{{ t("team.lede") }}</p>
      </div>
    </div>

    <div v-if="error" class="notice notice--danger" style="margin-bottom: var(--space-4)"><div class="notice__body">{{ error }}</div></div>
    <div v-if="notice" class="notice notice--success" style="margin-bottom: var(--space-4)"><div class="notice__body">{{ notice }}</div></div>

    <div v-if="manage" class="card">
      <h2 class="card__title">{{ t("team.inviteTitle") }}</h2>
      <p class="card__sub">{{ t("team.inviteLede") }}</p>
      <form class="team__invite" @submit.prevent="invite">
        <div class="field">
          <label class="field__label" for="tm-email">{{ t("auth.email") }}</label>
          <input id="tm-email" v-model.trim="form.email" class="input" type="email" required />
        </div>
        <div class="field">
          <label class="field__label" for="tm-role">{{ t("team.role") }}</label>
          <select id="tm-role" v-model="form.role" class="select">
            <option value="member">{{ t("provider.roles.member") }}</option>
            <option value="admin">{{ t("provider.roles.admin") }}</option>
          </select>
        </div>
        <button class="btn" type="submit" :disabled="busy || !form.email">
          <span v-if="busy" class="spinner" aria-hidden="true"></span>
          {{ t("team.invite") }}
        </button>
      </form>
      <p class="xs muted">{{ t("team.rolesHelp") }}</p>
    </div>

    <div class="card">
      <h2 class="card__title">{{ t("team.members") }}</h2>
      <div v-if="loading" class="row"><span class="spinner" aria-hidden="true"></span><span class="muted small">{{ t("common.loading") }}</span></div>
      <p v-else-if="!members.length" class="muted small">{{ t("team.empty") }}</p>
      <div v-else class="table__wrap">
        <table class="table">
          <thead>
            <tr>
              <th>{{ t("team.member") }}</th>
              <th>{{ t("team.role") }}</th>
              <th>{{ t("common.status") }}</th>
              <th>2FA</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="m in members" :key="m.user_id || m.id || m.email">
              <td>
                <div class="strong small">{{ m.name || m.email }}</div>
                <div v-if="m.name" class="xs muted">{{ m.email }}</div>
              </td>
              <td class="small">{{ roleLabel(m.role) }}</td>
              <td>
                <span :class="['badge', m.pending ? 'badge--info' : 'badge--success']">
                  {{ m.pending ? t("team.invited") : t("team.active") }}
                </span>
              </td>
              <td>
                <span v-if="!m.pending" :class="['badge', m.totp_enabled ? 'badge--success' : 'badge--warn']">
                  {{ m.totp_enabled ? t("common.yes") : t("common.no") }}
                </span>
              </td>
              <td>
                <button v-if="removable(m)" type="button" class="btn btn--ghost btn--sm" :disabled="busy" @click="remove(m)">
                  {{ t("common.remove") }}
                </button>
                <span v-else-if="m.user_id && m.user_id === session.user?.id" class="xs muted">{{ t("team.you") }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import { api } from "../lib/api";
import { session, canManageTeam } from "../lib/session";
import { useErrorText } from "../lib/errors";

const { t, te } = useI18n();
const errorText = useErrorText();

const members = ref([]);
const loading = ref(true);
const busy = ref(false);
const error = ref("");
const notice = ref("");
const form = reactive({ email: "", role: "member" });
const manage = computed(() => canManageTeam());

function normalise(data) {
  if (Array.isArray(data)) return data.map((m) => ({ ...m, pending: m.pending ?? !m.accepted_at }));
  const active = (data?.members || []).map((m) => ({ ...m, pending: false }));
  const invites = (data?.invites || []).map((i) => ({ ...i, pending: true }));
  return [...active, ...invites];
}

function roleLabel(role) {
  return te(`provider.roles.${role}`) ? t(`provider.roles.${role}`) : role;
}

function removable(m) {
  return manage.value && !!m.user_id && m.user_id !== session.user?.id && m.role !== "owner";
}

async function load() {
  loading.value = true;
  try {
    members.value = normalise(await api.team());
  } catch (e) {
    error.value = errorText(e);
  } finally {
    loading.value = false;
  }
}

async function run(fn) {
  busy.value = true;
  error.value = "";
  notice.value = "";
  try {
    await fn();
    await load();
  } catch (e) {
    error.value = errorText(e);
  } finally {
    busy.value = false;
  }
}

const invite = () => run(async () => {
  await api.inviteMember({ email: form.email, role: form.role });
  notice.value = t("team.invitedNotice", { email: form.email });
  form.email = "";
});

const remove = (m) => {
  if (!window.confirm(t("team.removeConfirm", { who: m.name || m.email }))) return;
  return run(() => api.removeMember(m.user_id));
};

onMounted(load);
</script>

<style scoped>
.team__invite { display: grid; grid-template-columns: 2fr 1fr auto; gap: var(--space-3); align-items: end; margin: var(--space-4) 0 var(--space-2); }
.team__invite .field { margin-bottom: 0; }
@media (max-width: 640px) {
  .team__invite { grid-template-columns: 1fr; }
}
</style>
